// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Returns LIST page (Stage 6P.8).
//
// Server-paginated list at /erp/purchase-returns.
//
// Columns (brief §K1):
//   Return No · Return Date · Supplier (+ GSTIN tag) · Return Type
//   Reason · Source Bill / GRN · Taxable · Total Tax · Grand Total
//   Inventory Posted · Voucher No · Status · Actions (⋯)
//
// Toolbar:
//   Supplier search · Date range (return_date) · Status filter
//   Return-type filter · Reason filter · Clear · Refresh · "Add" CTA
//
// Per-row actions:
//   • View / Edit → navigate to /erp/purchase-returns/:id
//   • Confirm     → only when status is DRAFT
//                   (backend transitions DRAFT → CONFIRMED)
//   • Post        → only when status is CONFIRMED or POSTING_FAILED
//                   (backend posts the DEBIT_NOTE voucher + emits the
//                    PURCHASE_RETURN_OUTWARD inventory movement +
//                    writes the ITC reversal rows)
//   • Cancel      → only when status is POSTED or POSTING_FAILED
//                   (backend reverses the voucher + emits
//                    PURCHASE_RETURN_OUTWARD_REVERSAL ledger rows +
//                    flips the ITC reversal rows to CANCELLED)
//
// Backend remains the authority for ALL return math:
//   return_no / debit_note_no allocation, supplier-payable / ITC
//   register impact, inventory_stock_ledger idempotency, debit-note
//   voucher balance enforcement, duplicate-block decisions.
// This page is a pure read + dispatch surface.
//
// Style:
//   Mirrors SupplierPaymentsPage — slate-50 header strip, 22 px row
//   density, sticky toolbar, ⋯ row-actions menu. Reuses the shared
//   GoodsReceiptsPage.css via the .grn-* wrapper so no new CSS file
//   is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, Modal, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined, PlusOutlined,
  ExclamationCircleOutlined, EyeOutlined, StopOutlined,
  CheckCircleOutlined, RollbackOutlined, EditOutlined
} from '@ant-design/icons';

import { purchaseReturnApi } from './purchaseReturnApi';
import {
  RETURN_STATUS,
  RETURN_TYPES,
  REASON_CODES,
  returnStatusLabel,
  returnStatusColor,
  returnTypeLabel,
  reasonCodeLabel,
  canEditPurchaseReturn,
  canConfirmPurchaseReturn,
  canPostPurchaseReturn,
  canCancelPurchaseReturn
} from './purchaseReturnHelpers';
import RoleGate from '../../../components/RoleGate';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const STATUS_OPTIONS = RETURN_STATUS.map((v) => ({
  value: v,
  label: returnStatusLabel(v)
}));

const RETURN_TYPE_OPTIONS = RETURN_TYPES.map((v) => ({
  value: v,
  label: returnTypeLabel(v)
}));

const REASON_OPTIONS = REASON_CODES.map((v) => ({
  value: v,
  label: reasonCodeLabel(v)
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Choose the most-precise source-document label — Purchase Bill No
// when present, then GRN No, then PO No. Operators see whichever
// upstream the return links to without overstuffing the column.
function readSourceDoc(row) {
  return (
    pickField(row, 'purchase_bill_no', 'purchaseBillNo')
    || pickField(row, 'grn_no',        'grnNo')
    || pickField(row, 'po_no',         'poNo')
    || null
  );
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseReturnsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.purchase.returns.*` strings are the Stage 6P.8 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView    = hasPerm('erp.purchase.returns.view');
  const canCreate  = hasPerm('erp.purchase.returns.create');
  const canUpdate  = hasPerm('erp.purchase.returns.update');
  const canConfirm = hasPerm('erp.purchase.returns.confirm');
  const canPost    = hasPerm('erp.purchase.returns.post');
  const canCancel  = hasPerm('erp.purchase.returns.cancel');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [supplier,    setSupplier]    = useState('');
  const [status,      setStatus]      = useState(null);
  const [returnType,  setReturnType]  = useState(null);
  const [reasonCode,  setReasonCode]  = useState(null);
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the supplier-payment list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` and `search` — backend tolerates the
      // unused one via stripUnknown:true on its Joi validator.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      const supplierNeedle = (supplier || '').trim();
      if (supplierNeedle) {
        params.supplier     = supplierNeedle;
        params.supplierName = supplierNeedle;
      }
      if (status)     params.status     = status;
      if (returnType) params.returnType = returnType;
      if (reasonCode) params.reasonCode = reasonCode;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseReturnApi.list(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load purchase returns.'),
        key: 'pr-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, supplier, status, returnType, reasonCode, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus     = (v) => { setStatus(v || null);     setPage(1); };
  const onReturnType = (v) => { setReturnType(v || null); setPage(1); };
  const onReason     = (v) => { setReasonCode(v || null); setPage(1); };
  const onDates      = (v) => { setDateRange(v || null);  setPage(1); };
  const onSupplier   = (e) => { setSupplier(e.target.value); setPage(1); };
  const onReset = () => {
    setSearchInput('');
    setSearch('');
    setSupplier('');
    setStatus(null);
    setReturnType(null);
    setReasonCode(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase-returns/${encodeURIComponent(id)}`);
  }, [navigate]);

  const goToNew = useCallback(() => {
    navigate('/erp/purchase-returns/new');
  }, [navigate]);

  // ── Confirm handler — confirmed via Modal with an optional
  // remarks prompt. Backend transitions DRAFT → CONFIRMED.
  const runConfirm = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let remarks = '';
    Modal.confirm({
      title: 'Confirm purchase return?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This locks the line balances on return{' '}
            <strong>{pickField(row, 'return_no', 'returnNo') || id}</strong>{' '}
            and makes it ready to post the debit-note voucher. Provide a
            short note (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Remarks (optional)"
            onChange={(e) => { remarks = e.target.value; }}
          />
        </div>
      ),
      okText: 'Confirm Return',
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          const payload = remarks && remarks.trim() ? { remarks: remarks.trim() } : {};
          await purchaseReturnApi.confirm(id, payload);
          message.success('Return confirmed.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to confirm return.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Post handler — confirmed via Modal with an optional remarks
  // prompt. Backend posts the DEBIT_NOTE voucher + writes the
  // PURCHASE_RETURN_OUTWARD inventory movement + writes ITC reversal
  // rows. UI never wins.
  const runPost = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let remarks = '';
    Modal.confirm({
      title: 'Post purchase return?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will post return{' '}
            <strong>{pickField(row, 'return_no', 'returnNo') || id}</strong>{' '}
            as a debit note to the general ledger, write the outward
            inventory movement, and reverse the related ITC. Provide
            a short note (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Remarks (optional)"
            onChange={(e) => { remarks = e.target.value; }}
          />
        </div>
      ),
      okText: 'Post Return',
      cancelText: 'Cancel',
      async onOk() {
        setBusyId(id);
        try {
          const payload = remarks && remarks.trim() ? { remarks: remarks.trim() } : {};
          await purchaseReturnApi.post(id, payload);
          message.success('Return posted.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to post return.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Cancel handler — confirmed via Modal with a reason prompt ─
  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel purchase return?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will reverse the debit-note voucher, emit the
            inventory reversal, and flip the ITC reversal rows on
            return{' '}
            <strong>{pickField(row, 'return_no', 'returnNo') || id}</strong>.
            Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel Return',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseReturnApi.cancel(id, reason || undefined);
          message.success('Return cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel return.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    const status = pickField(row, 'status', 'status');

    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View'
    });

    if (canUpdate && canEditPurchaseReturn(status)) {
      items.push({
        key:   'edit',
        icon:  <EditOutlined />,
        label: 'Edit'
      });
    }

    if (canConfirm && canConfirmPurchaseReturn(status)) {
      items.push({ type: 'divider' });
      items.push({
        key:   'confirm',
        icon:  <CheckCircleOutlined />,
        label: 'Confirm'
      });
    }

    if (canPost && canPostPurchaseReturn(status)) {
      items.push({ type: 'divider' });
      items.push({
        key:   'post',
        icon:  <RollbackOutlined />,
        label: 'Post'
      });
    }

    if (canCancel && canCancelPurchaseReturn(status)) {
      items.push({ type: 'divider' });
      items.push({
        key:    'cancel',
        icon:   <StopOutlined />,
        label:  'Cancel',
        danger: true
      });
    }
    return items;
  }, [canUpdate, canConfirm, canPost, canCancel]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':
      case 'edit':    return openView(row);
      case 'confirm': return runConfirm(row);
      case 'post':    return runPost(row);
      case 'cancel':  return runCancel(row);
      default:        return undefined;
    }
  }, [openView, runConfirm, runPost, runCancel]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Return No',
      dataIndex: 'returnNo',
      width:     180,
      render: (_v, row) => {
        const no = pickField(row, 'return_no', 'returnNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Return Date',
      dataIndex: 'returnDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'return_date', 'returnDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Return Type',
      dataIndex: 'returnType',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'return_type', 'returnType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {returnTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Reason',
      dataIndex: 'reasonCode',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'reason_code', 'reasonCode');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {reasonCodeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Source Doc',
      dataIndex: 'sourceDoc',
      width:     160,
      render: (_v, row) => {
        const src = readSourceDoc(row);
        if (!src) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{src}</span>;
      }
    },
    {
      title:     'Taxable',
      dataIndex: 'taxableAmount',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'taxable_amount', 'taxableAmount'))}
        </span>
      )
    },
    {
      title:     'Total Tax',
      dataIndex: 'totalTax',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'total_tax', 'totalTax'))}
        </span>
      )
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'grand_total', 'grandTotal'))}
        </span>
      )
    },
    {
      title:     'Inventory',
      dataIndex: 'inventoryPosted',
      width:     110,
      render: (_v, row) => {
        const posted = !!pickField(row, 'inventory_posted', 'inventoryPosted');
        return (
          <Tag
            color={posted ? 'success' : 'default'}
            className="grn-status-tag"
          >
            {posted ? 'Posted' : 'Pending'}
          </Tag>
        );
      }
    },
    {
      title:     'Voucher No',
      dataIndex: 'accountingVoucherNo',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'accounting_voucher_no', 'accountingVoucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'status', 'status');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">Draft</Tag>
        );
        return (
          <Tag color={returnStatusColor(v)} className="grn-status-tag">
            {returnStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openView, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view purchase returns."
          description="Ask your master account to grant erp.purchase.returns.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><RollbackOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Purchase Returns</span>
            <span className="grn-headstrip__subtitle">
              Record purchase returns, post debit notes, and reverse ITC.
              Backend owns return-no allocation, supplier-payable impact,
              inventory ledger idempotency, voucher posting, and ITC
              reversal — this surface reads and dispatches confirm / post /
              cancel actions only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
          <RoleGate>
            {canCreate ? (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={goToNew}
              >
                Add Purchase Return
              </Button>
            ) : null}
          </RoleGate>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search return no, debit note, bill, GRN…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Supplier name / GSTIN…"
          value={supplier}
          onChange={onSupplier}
          style={{ width: 220 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 160 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All types"
          value={returnType || undefined}
          onChange={onReturnType}
          options={RETURN_TYPE_OPTIONS}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All reasons"
          value={reasonCode || undefined}
          onChange={onReason}
          options={REASON_OPTIONS}
          style={{ width: 180 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'return_no', 'returnNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No purchase returns yet.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
